import React, { useState, useEffect, useMemo, useCallback, useRef, useId, Fragment } from 'react';
import { createPortal } from 'react-dom';
import Icon from "../icons/Icon";
import { useParams, useNavigate } from 'react-router-dom';
import { useCompanyValidation } from '../../hooks/useCompanyValidation';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadRunEmployees, getRunSummary, runEmployeeCount } from '../../services/payrollRunStore';
import ConfirmModal from '../modals/ConfirmModal';
import Modal from '../modals/Modal';
import PayrollRunExpansion from './PayrollRunExpansion';
import { generateCsvContent, templateHasAccountFields, getUsableFields } from '../../constants/csvFieldDefinitions';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/sections/payroll.css';
import { toYMDString } from '../../utils/periodDate';

const PayrollList = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { company, loading: companyLoading } = useCompanyValidation(slug);
  const { hasPermission } = useAuth();
  const canProcessPayroll = hasPermission('payroll:run');
  const canViewPayslips = hasPermission('payslip:view');
  const toast = useToast();

  const [payrollRuns, setPayrollRuns] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [downloadingStatus, setDownloadingStatus] = useState({});

  // Delete draft state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  // CSV Modal state
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [selectedRunForCsv, setSelectedRunForCsv] = useState(null);
  const csvTitleId = useId();
  const errorTitleId = useId();
  const [csvTemplates, setCsvTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  // Actions dropdown state. The dropdown is PORTALLED to <body> with fixed
  // coordinates: .pl-table-container sets overflow-x:auto for the horizontal
  // scroll, which makes it a clipping box, so an absolutely-positioned menu on
  // the last row is cut off at the footer. CSS computes a `visible` axis to
  // `auto` when the other is not visible, so the overflow cannot just be
  // relaxed. Same treatment as the Loans page's kebab (/test-impl 2026-09-26).
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAt, setMenuAt] = useState(null);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);

  const placeMenu = (btn) => {
    const r = btn?.getBoundingClientRect();
    if (!r) return;
    const H = 110; // two items; only decides which side to open
    const below = window.innerHeight - r.bottom;
    setMenuAt({
      position: 'fixed',
      zIndex: 1000,
      // 'auto', not undefined: React omits an undefined style property, which
      // would leave .pl-action-dropdown's own `top: calc(100% + 4px)` to resolve
      // against the viewport and push the menu off screen — the upward branch is
      // exactly the near-the-footer case the portal exists for (/impl-review r2).
      top: below < H ? 'auto' : r.bottom + 4,
      bottom: below < H ? window.innerHeight - r.top + 4 : undefined,
      right: window.innerWidth - r.right,
    });
  };

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e) => {
      if (!menuRef.current?.contains(e.target) && !menuBtnRef.current?.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    const shut = () => setOpenMenuId(null);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', shut);
    window.addEventListener('scroll', shut, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', shut);
      window.removeEventListener('scroll', shut, true);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (company) {
      fetchPayrollRuns();
      fetchCsvTemplates();
    }
  }, [company]);

  const fetchCsvTemplates = async () => {
    if (!company) return;
    try {
      const docRef = doc(db, 'companies', company.id, 'settings', 'csvTemplates');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        setCsvTemplates(snap.data().templates || []);
      }
    } catch (err) {
      console.error('[PayrollList] Error fetching CSV templates:', err);
    }
  };

  const fetchPayrollRuns = async () => {
    try {
      setLoading(true);

      // Fetch all four statuses: draft, pending-approval, completed
      // (PRD_PayrollApproval: pending-approval runs must appear on the list)
      // + 'deleting' — a delete that crashed mid-way leaves this tombstone
      // (PRD_StandaloneAdjustments C21); without a visible row the run would
      // silently keep its index entry, DTR locks, and period conflicts with
      // no way to retry outside a script (impl-review r2 #3).
      // + users, to resolve the rejecter name on "Rejected" draft chips.
      const [completedSnap, draftSnap, pendingSnap, deletingSnap, userSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'companies', company.id, 'payrollRuns'),
          where('status', '==', 'completed'),
          orderBy('lastModified', 'desc')
        )),
        getDocs(query(
          collection(db, 'companies', company.id, 'payrollRuns'),
          where('status', '==', 'draft'),
          orderBy('lastModified', 'desc')
        )),
        getDocs(query(
          collection(db, 'companies', company.id, 'payrollRuns'),
          where('status', '==', 'pending-approval'),
          orderBy('lastModified', 'desc')
        )),
        getDocs(query(
          collection(db, 'companies', company.id, 'payrollRuns'),
          where('status', '==', 'deleting'),
          orderBy('lastModified', 'desc')
        )),
        getDocs(collection(db, 'companies', company.id, 'users')),
      ]);

      const uMap = {};
      userSnap.forEach((d) => {
        const data = d.data();
        const name = data.fullName || data.displayName || data.email || 'Unknown User';
        uMap[d.id] = name;                                  // by users-doc id (Phase-1 userDocId)
        if (data.firebaseUid) uMap[data.firebaseUid] = name; // by auth uid (pre-migration admin userDocId)
      });
      setUserMap(uMap);

      const parseRun = (docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          periodStart: data.periodStart?.toDate ? data.periodStart.toDate() : new Date(data.periodStart),
          periodEnd: data.periodEnd?.toDate ? data.periodEnd.toDate() : new Date(data.periodEnd),
          lastModified: data.lastModified?.toDate ? data.lastModified.toDate() : null
        };
      };

      const deleting = [];
      deletingSnap.forEach((docSnap) => deleting.push(parseRun(docSnap)));

      const drafts = [];
      draftSnap.forEach((docSnap) => drafts.push(parseRun(docSnap)));

      const pending = [];
      pendingSnap.forEach((docSnap) => pending.push(parseRun(docSnap)));

      const completed = [];
      completedSnap.forEach((docSnap) => completed.push(parseRun(docSnap)));

      // Stuck deletes first (they need attention), then drafts, pending, completed
      setPayrollRuns([...deleting, ...drafts, ...pending, ...completed]);
    } catch (err) {
      console.error('[PayrollList] Error fetching payroll runs:', err);
      toast.error('Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  };

  // Delete draft handler
  const handleDeleteDraft = async () => {
    if (!draftToDelete || !company) return;
    setDeletingDraft(true);
    try {
      const runId = draftToDelete.runId || draftToDelete.id;

      // C6 (PRD_PayrollSavePerf): delete SERVER-SIDE via the callable — the client batched
      // delete saturates the Web SDK write stream at scale ("Transaction too big" on a
      // ~698-doc subcollection). The callable prunes the payrollIndex entry FIRST (releasing
      // the derived DTR lock), then recursiveDeletes the run doc + its employees subcollection.
      const functions = getFunctions(undefined, 'asia-southeast1');
      await httpsCallable(functions, 'deletePayrollRun')({ runId });
      setPayrollRuns(prev => prev.filter(r => (r.runId || r.id) !== runId));
      toast.success(draftToDelete.status === 'deleting'
        ? 'Payroll run removed'
        : 'Draft deleted successfully');
    } catch (err) {
      console.error('[PayrollList] Error deleting draft:', err);
      toast.error('Failed to delete draft. Please try again.');
    } finally {
      setDeletingDraft(false);
      setShowDeleteModal(false);
      setDraftToDelete(null);
    }
  };

  // Calculate summary statistics from completed runs only
  const summaryStats = useMemo(() => {
    let totalDeductions = 0;
    let totalLoans = 0;
    let totalNet = 0;
    let totalEmployees = 0;
    let completedRuns = 0;
    let draftCount = 0;

    payrollRuns.forEach(run => {
      if (run.status === 'draft') {
        draftCount++;
        return;
      }
      // Only count fully completed runs in the summary stats — pending-approval
      // runs are excluded since their figures aren't yet finalized.
      if (run.status !== 'completed') return;
      completedRuns++;
      const runSummary = getRunSummary(run);
      totalDeductions += runSummary.totalDeductions;
      totalLoans += runSummary.totalLoans || 0; // absent on pre-loans parents (PRD_LoansManagement C8k)
      totalNet += runSummary.totalNet;
      totalEmployees += runSummary.totalEmployees;
    });

    return {
      totalRuns: completedRuns,
      draftCount,
      totalEmployees,
      totalDeductions,
      totalLoans,
      totalNet
    };
  }, [payrollRuns]);

  // Calculate days inclusive of both start and end dates
  const calculateDays = (periodStart, periodEnd) => {
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleRowClick = (runId) => {
    setExpandedRowId(expandedRowId === runId ? null : runId);
  };

  const handleCsvClick = (e, run) => {
    e.stopPropagation();

    // No templates configured → show error modal
    if (csvTemplates.length === 0) {
      setErrorModalOpen(true);
      return;
    }

    setSelectedRunForCsv(run);
    // Pre-select the default template (or first)
    const defaultTpl = csvTemplates.find(t => t.isDefault) || csvTemplates[0];
    setSelectedTemplateId(defaultTpl.id);
    setCsvModalOpen(true);
  };

  const selectedTemplate = csvTemplates.find(t => t.id === selectedTemplateId);
  // What the file will actually contain. Templates saved before a column was
  // retired can end up with every enabled id retired, leaving nothing to export
  // — the download is blocked below rather than emitting a blank file (BL-084).
  // getUsableFields tolerates an undefined template (none selected yet).
  const usableFields = getUsableFields(selectedTemplate);

  const handleCsvDownload = async () => {
    if (!selectedRunForCsv || !selectedTemplate) return;

    const run = selectedRunForCsv;
    setDownloadingStatus(prev => ({ ...prev, [`csv-${run.id}`]: true }));

    try {
      let payrollData = run;
      if (!run.employees || run.employees.length === 0) {
        // Converted runs carry no inline employees — load the subcollection
        // (loadRunEmployees returns the inline array for historical runs).
        const employees = await loadRunEmployees(company.id, run.id, run);
        payrollData = { ...run, employees };
      }

      const csvContent = generateCsvContent(payrollData.employees, selectedTemplate);

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payroll_${payrollData.runId || payrollData.id}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCsvModalOpen(false);
      setSelectedRunForCsv(null);
    } catch (err) {
      console.error('[PayrollList] Error generating CSV:', err);
      toast.error('Failed to generate CSV');
    } finally {
      setDownloadingStatus(prev => ({ ...prev, [`csv-${run.id}`]: false }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0).replace('PHP', '₱');
  };

  const formatPeriod = (start, end) => {
    const options = { month: 'short', day: 'numeric' };
    const yearOptions = { year: 'numeric' };
    const startStr = new Intl.DateTimeFormat('en-US', options).format(start);
    const endStr = new Intl.DateTimeFormat('en-US', options).format(end);
    const year = new Intl.DateTimeFormat('en-US', yearOptions).format(end);
    return `${startStr} - ${endStr}, ${year}`;
  };

  if (companyLoading) {
    return (
      <div className="pl-loading">
        <div className="pl-loading__spinner"></div>
        <p className="pl-loading__text">Loading...</p>
      </div>
    );
  }

  if (!company) {
    return <div className="error-message">Company not found</div>;
  }

  return (
    <div className="payroll-list">
      {/* Header */}
      <div className="pl-header">
        <h1 className="pl-page-title">Payroll Runs</h1>
        {canProcessPayroll && (
          <button
            className="btn-primary"
            onClick={() => navigate(`/company/${slug}/payroll/process`)}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.8125rem",
              fontWeight: "600",
              borderRadius: "8px",
            }}
          >
            + New Payroll Run
          </button>
        )}
      </div>

      {/* Summary Stats Cards - Mobile only */}
      {isMobile && (
        <div className="pl-stats">
          <div className="pl-stat-card pl-stat-card--runs">
            <div className="pl-stat-card__label">Total Runs</div>
            <div className="pl-stat-card__value">{summaryStats.totalRuns}</div>
          </div>
          <div className="pl-stat-card pl-stat-card--employees">
            <div className="pl-stat-card__label">Employees Paid</div>
            <div className="pl-stat-card__value">{summaryStats.totalEmployees}</div>
          </div>
          <div className="pl-stat-card pl-stat-card--deductions">
            <div className="pl-stat-card__label">Government Contributions</div>
            <div className="pl-stat-card__value">{formatCurrency(summaryStats.totalDeductions)}</div>
          </div>
          <div className="pl-stat-card pl-stat-card--deductions">
            <div className="pl-stat-card__label">Total Loans</div>
            <div className="pl-stat-card__value">{formatCurrency(summaryStats.totalLoans)}</div>
          </div>
          <div className="pl-stat-card pl-stat-card--net">
            <div className="pl-stat-card__label">Total Net Pay</div>
            <div className="pl-stat-card__value">{formatCurrency(summaryStats.totalNet)}</div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="pl-loading">
          <div className="pl-loading__spinner"></div>
          <p className="pl-loading__text">Loading payroll runs...</p>
        </div>
      ) : payrollRuns.length === 0 ? (
        /* Empty State */
        <div className="pl-empty-state">
          <div className="pl-empty-state__icon"><Icon name="bar-chart-3" size={32} /></div>
          <h3 className="pl-empty-state__title">No payroll runs yet</h3>
          <p className="pl-empty-state__message">Process your first payroll to see it here</p>
          {canProcessPayroll && (
            <button
              className="btn-primary"
              onClick={() => navigate(`/company/${slug}/payroll/process`)}
            >
              <span>+</span> Create Payroll Run
            </button>
          )}
        </div>
      ) : (
        /* Table */
        <div className="pl-table-container">
          <table className="pl-table">
            <thead>
              <tr>
                <th className="th-period">Period</th>
                <th className="th-run-id">Run ID</th>
                <th className="th-days">Days</th>
                <th className="th-employees">Employees</th>
                <th className="th-net-pay">Net Pay</th>
                <th className="th-status">Status</th>
                <th className="pl-th-date">Date</th>
                <th className="pl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrollRuns.map((run) => {
                const isDraft = run.status === 'draft';
                const isPending = run.status === 'pending-approval';
                const isCompleted = run.status === 'completed';
                const isDeleting = run.status === 'deleting'; // crashed-delete tombstone (C21)
                const isInert = isDraft || isDeleting; // no expand, no row click
                const hasRejection = isDraft && !!run.lastRejection;
                return (
                <Fragment key={run.id}>
                  {/* Main Row */}
                  <tr
                    className={`pl-row ${expandedRowId === run.id ? 'expanded' : ''} ${isInert ? 'pl-row--draft' : ''}`}
                    onClick={() => isInert ? null : handleRowClick(run.id)}
                    style={isInert ? { cursor: 'default' } : undefined}
                  >
                    <td className="td-period">
                      {!isInert && <span className="expand-icon"><Icon name="chevron-right" size={14} /></span>}
                      {formatPeriod(run.periodStart, run.periodEnd)}
                      {hasRejection && (
                        <span
                          title={`Rejected by ${userMap[run.lastRejection.by] || run.lastRejection.by || 'Unknown'}: ${run.lastRejection.reason || ''}`}
                          style={{
                            marginLeft: 8, fontSize: 11, padding: '2px 6px',
                            background: 'var(--ui-danger-tint)', color: 'var(--ui-danger-text)',
                            border: '1px solid var(--ui-danger)', borderRadius: 4,
                            fontWeight: 500,
                          }}
                        >
                          Rejected
                        </span>
                      )}
                    </td>
                    <td className="td-run-id">{run.runId || run.id}</td>
                    <td className="td-days">
                      {isInert && !runEmployeeCount(run) ? '—' : calculateDays(run.periodStart, run.periodEnd)}
                    </td>
                    <td className="td-employees">{runEmployeeCount(run)}</td>
                    <td className="td-net-pay">
                      {isInert ? '—' : formatCurrency(getRunSummary(run).totalNet)}
                    </td>
                    <td className="td-status">
                      {isDraft && (
                        <span className="pl-status-badge pl-status-badge--draft">Draft</span>
                      )}
                      {isDeleting && (
                        <span
                          className="pl-status-badge"
                          style={{
                            background: 'var(--ui-danger-tint)', color: 'var(--ui-danger-text)',
                            border: '1px solid var(--ui-danger)',
                          }}
                        >
                          Deleting…
                        </span>
                      )}
                      {isPending && (
                        <span
                          className="pl-status-badge"
                          style={{
                            background: 'var(--ui-accent-tint)', color: 'var(--ui-warning-text)',
                            border: '1px solid var(--ui-accent)',
                          }}
                        >
                          Pending Approval
                        </span>
                      )}
                      {isCompleted && (
                        <span className="pl-status-badge pl-status-badge--completed">Completed</span>
                      )}
                    </td>
                    <td className="td-date">{toYMDString(run.lastModified) || "N/A"}</td>
                    <td className="pl-td-actions">
                      <div className="action-buttons">
                        {isDeleting ? (
                          canProcessPayroll ? (
                            <button
                              className="btn-action btn-delete-draft"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDraftToDelete(run);
                                setShowDeleteModal(true);
                              }}
                            >
                              Retry Delete
                            </button>
                          ) : null
                        ) : isDraft ? (
                          canProcessPayroll ? (
                            <>
                              <button
                                className="btn-action btn-resume"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/company/${slug}/payroll/process?draft=${run.runId || run.id}`);
                                }}
                              >
                                Resume
                              </button>
                              <button
                                className="btn-action btn-delete-draft"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDraftToDelete(run);
                                  setShowDeleteModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </>
                          ) : null
                        ) : isCompleted ? (
                          <div className="pl-action-menu">
                            <button
                              className="btn-action btn-menu"
                              ref={openMenuId === run.id ? menuBtnRef : null}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openMenuId !== run.id) placeMenu(e.currentTarget);
                                setOpenMenuId(openMenuId === run.id ? null : run.id);
                              }}
                              aria-label="Actions"
                            >
                              <Icon name="more-vertical" size={16} />
                            </button>
                            {openMenuId === run.id && menuAt && createPortal(
                              <div className="pl-action-dropdown" ref={menuRef} style={menuAt} onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="pl-action-dropdown__item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    handleCsvClick(e, run);
                                  }}
                                  disabled={downloadingStatus[`csv-${run.id}`]}
                                >
                                  {downloadingStatus[`csv-${run.id}`] ? 'Downloading...' : 'Download CSV'}
                                </button>
                                {canViewPayslips && (
                                  <button
                                    className="pl-action-dropdown__item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                      navigate(`/company/${slug}/payroll/payslips/${run.runId || run.id}`);
                                    }}
                                  >
                                    View Payslips
                                  </button>
                                )}
                              </div>,
                              document.body,
                            )}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRowId === run.id && (
                    <tr className="pl-expansion-row">
                      <td colSpan="8">
                        <PayrollRunExpansion run={run} companyId={company.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Footer */}
          <div className="pl-footer">
            <div className="pl-footer-stats">
              <div className="pl-footer-stat">
                <span className="pl-footer-stat__label">Completed</span>
                <span className="pl-footer-stat__value">{summaryStats.totalRuns}</span>
              </div>
              {summaryStats.draftCount > 0 && (
                <div className="pl-footer-stat">
                  <span className="pl-footer-stat__label">Drafts</span>
                  <span className="pl-footer-stat__value" style={{ color: 'var(--ui-accent)' }}>{summaryStats.draftCount}</span>
                </div>
              )}
              <div className="pl-footer-stat">
                <span className="pl-footer-stat__label">Employees Paid</span>
                <span className="pl-footer-stat__value">{summaryStats.totalEmployees}</span>
              </div>
              <div className="pl-footer-stat">
                <span className="pl-footer-stat__label">Government Contributions</span>
                <span className="pl-footer-stat__value">{formatCurrency(summaryStats.totalDeductions)}</span>
              </div>
              <div className="pl-footer-stat">
                <span className="pl-footer-stat__label">Total Loans</span>
                <span className="pl-footer-stat__value">{formatCurrency(summaryStats.totalLoans)}</span>
              </div>
              <div className="pl-footer-stat pl-footer-stat--total">
                <span className="pl-footer-stat__label">Total Net Pay</span>
                <span className="pl-footer-stat__value">{formatCurrency(summaryStats.totalNet)}</span>
              </div>
            </div>
            <div className="pl-footer-info">
              Click on a row to expand details
            </div>
          </div>
        </div>
      )}

      {/* CSV Download Modal */}
      {csvModalOpen && selectedRunForCsv && selectedTemplate && (
        <Modal
          isOpen
          onClose={() => setCsvModalOpen(false)}
          busy={!!downloadingStatus[`csv-${selectedRunForCsv.id}`]}
          labelledBy={csvTitleId}
          className="modal"
        >
            <div className="modal-header">
              <div className="modal-header__title" id={csvTitleId}>
                <span className="modal-header__icon"><Icon name="file-text" size={18} /></span>
                Download Payroll CSV
              </div>
            </div>
            <div className="modal-body">
              {/* Payroll Run Info */}
              <div className="modal-section">
                <div className="payroll-details-row">
                  <div className="payroll-detail-item">
                    <span className="payroll-detail-item__label">Run ID</span>
                    <span className="payroll-detail-item__value payroll-detail-item__value--mono">
                      {selectedRunForCsv.runId || selectedRunForCsv.id}
                    </span>
                  </div>
                  <div className="payroll-detail-divider"></div>
                  <div className="payroll-detail-item">
                    <span className="payroll-detail-item__label">Period</span>
                    <span className="payroll-detail-item__value">
                      {formatPeriod(selectedRunForCsv.periodStart, selectedRunForCsv.periodEnd)}
                    </span>
                  </div>
                  <div className="payroll-detail-divider"></div>
                  <div className="payroll-detail-item">
                    <span className="payroll-detail-item__label">Employees</span>
                    <span className="payroll-detail-item__value payroll-detail-item__value--highlight">
                      {runEmployeeCount(selectedRunForCsv)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Template Selector */}
              <div className="modal-section">
                <div className="modal-section__label">Export Template</div>
                <div className="template-row">
                  <select
                    value={selectedTemplateId || ''}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                  >
                    {csvTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </select>
                  {selectedTemplate.isDefault && (
                    <span className="template-default-badge">Default</span>
                  )}
                </div>
              </div>

              {/* Column Summary */}
              <div className="modal-section">
                <div className="column-summary">
                  {/* Retired ids are already dropped, so this count matches the
                      downloaded file — both read getUsableFields (BL-007/BL-084). */}
                  <span className="column-summary__count">{usableFields.length} columns:</span>{' '}
                  <span className="column-summary__list">{usableFields.map(f => f.label).join(', ')}</span>
                </div>
              </div>

              {/* Nothing left to export — every enabled column of this template
                  has had its definition retired. Downloading would produce a
                  blank header and blank rows, so it is blocked below (BL-084).
                  Re-saving the template in Settings repairs it. */}
              {usableFields.length === 0 && (
                <div className="modal-section" style={{ marginBottom: 0 }}>
                  <div className="csv-warning">
                    <span className="csv-warning__icon"><Icon name="alert-triangle" size={16} /></span>
                    <span className="csv-warning__text">
                      This template has no columns left to export. Open it in Settings &gt; CSV Export Layout and choose the columns you need.
                    </span>
                  </div>
                </div>
              )}

              {/* Account Number Warning (only when leading-zero preservation is OFF) */}
              {templateHasAccountFields(selectedTemplate.columns) && !selectedTemplate.preserveLeadingZeros && (
                <div className="modal-section" style={{ marginBottom: 0 }}>
                  <div className="csv-warning">
                    <span className="csv-warning__icon"><Icon name="alert-triangle" size={16} /></span>
                    <span className="csv-warning__text">
                      Account numbers may look shortened in Excel (e.g., 2.41201E+11). Don't worry — the full values are intact and will copy-paste correctly into your bank file. To preserve leading zeros, enable that option in Settings &gt; CSV Export Layout.
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setCsvModalOpen(false)}
                disabled={!!downloadingStatus[`csv-${selectedRunForCsv.id}`]}
              >
                Cancel
              </button>
              <button
                className="btn-download"
                onClick={handleCsvDownload}
                disabled={downloadingStatus[`csv-${selectedRunForCsv.id}`] || usableFields.length === 0}
              >
                <span><Icon name="download" size={16} /></span> Download CSV
              </button>
            </div>
        </Modal>
      )}

      {/* CSV Not Configured Error Modal */}
      {errorModalOpen && (
        <Modal isOpen onClose={() => setErrorModalOpen(false)} labelledBy={errorTitleId} className="modal">
            <div className="modal-header">
              <div className="modal-header__title" id={errorTitleId}>
                <span className="modal-header__icon"><Icon name="alert-triangle" size={18} /></span>
                CSV Export Not Configured
              </div>
            </div>
            <div className="error-modal-body">
              <div className="error-modal-body__icon"><Icon name="file-text" size={32} /></div>
              <div className="error-modal-body__title">No CSV template found</div>
              <div className="error-modal-body__text">
                No CSV template has been set up yet. Please go to Settings &gt; CSV Export Layout to create a template and set it as the default.
              </div>
              <div className="error-modal-body__actions">
                <button className="btn-secondary" onClick={() => setErrorModalOpen(false)}>Cancel</button>
                <button
                  className="btn-go-settings"
                  onClick={() => {
                    setErrorModalOpen(false);
                    navigate(`/company/${slug}/settings/csv-export`);
                  }}
                >
                  Go to Settings
                </button>
              </div>
            </div>
        </Modal>
      )}
      {/* Delete Draft Confirmation Modal (retry wording for a stuck 'deleting'
          tombstone — that run is not a draft and there is nothing to keep) */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDraftToDelete(null); }}
        onConfirm={handleDeleteDraft}
        title={draftToDelete?.status === 'deleting' ? 'Finish Deleting Payroll Run' : 'Delete Draft Payroll'}
        message={draftToDelete?.status === 'deleting'
          ? 'This delete was interrupted before it finished. Retry to finish removing the run and release its locks.'
          : 'Are you sure you want to discard this draft? All entered data (deductions, adjustments) will be lost.'}
        confirmText={draftToDelete?.status === 'deleting' ? 'Retry Delete' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
        loading={deletingDraft}
      />
    </div>
  );
};

export default PayrollList;
